"use client";

import React, { useRef } from "react";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableTextBlock } from "sanity";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GROQButton } from "@/sanity/lib/definitions";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { CTAButton } from "@/shadcn/ui/button";
import { motion, useScroll, useTransform } from "framer-motion";

import bg from "@/public/global/bg-art.jpg";
import logo from "@/public/logo-black.svg";
import arrow from "@/public/global/dotted-arrow.svg";
import Countdown from "@/components/global/countdown/Countdown";
import futuresHero from "@/public/futures/hero.png";
import knight from "@/public/global/knight.png";

export default function LastCTASection({
  chip,
  text,
  cta,
  className,
  textClassName,
  ctaClassName,
  partnerName,
  toggleCountdown,
  countdown,
  countdownLang,
  futures = false,
  hidePillars = false,
}: {
  chip?: string;
  text: PortableTextBlock[];
  cta: GROQButton;
  className?: string;
  textClassName?: string;
  ctaClassName?: string;
  partnerName?: string;
  toggleCountdown?: boolean;
  countdown?: string;
  countdownLang?: "de" | "en";
  futures?: boolean;
  hidePillars?: boolean;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const ySolana = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);
  const yArcher = useTransform(scrollYProgress, [0, 1], ["-2%", "2%"]);

  return (
    <section
      ref={parallaxRef}
      className={cn("relative overflow-clip px-[2rem] lg:px-[5rem]", className)}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[7rem] lg:pt-[13rem]">
        <div
          className={cn(
            "bg-gradient-to-b absolute left-0 top-0 w-full h-full",
            futures
              ? "from-[#FBFBFB] -from-[34%] to-[#E6E7F2]"
              : "from-[#F6F9F7] to-[#E6F2EB]",
          )}
        >
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.36]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container px-[3rem] flex flex-col items-center relative z-10">
          {partnerName && (
            <div className="flex gap-[2rem] items-center mb-[2.62rem]">
              <Image src={logo} alt="" className="w-[16.25rem]" />

              <Image src={arrow} alt="" className="flex-shrink-0" />
              <div className="flex items-center gap-[1rem]">
                <p className="text-[#103037] text-[2rem] lg:text-[2.25rem] font-[500] leading-[116%] m-0">
                  {partnerName}
                </p>
              </div>
            </div>
          )}
          {chip && (
            <p className="text-black/70 font-medium mb-[1.5rem] lg:mb-[2rem] text-[1.75rem] lg:text-[2rem]">
              {chip}
            </p>
          )}

          {text && (
            <div
              className={cn(
                "richtext w-[min(80rem,100%)] prose-h2:w-[40rem] sm:prose-h2:w-auto",
                textClassName,
              )}
            >
              <PortableText value={text} components={richTextComponent} />
            </div>
          )}

          {cta && (
            <CTAButton
              className={cn("mt-[3rem]", ctaClassName)}
              data={cta}
            />
          )}

          {toggleCountdown && countdown && (
            <div className="w-[min(62.625rem,100%)] mt-[5rem] rounded-[1.5rem] lg:rounded-[2.25rem] border border-[#10303729] prose-h2:text-[#103037] prose-h6:text-[#103037]/70">
              <Countdown
                dateTimeString={countdown}
                lang={countdownLang ?? "de"}
              />
            </div>
          )}

          {!futures && !hidePillars && (
            <>
              <CryptoPillar
                className="left-[calc(50%-21rem)] lg:left-[calc(50%-38rem)] h-[25rem]"
                type="ethereum"
                style={{ y: yEthereum }}
              />
              <CryptoPillar
                className="bottom-[5rem] lg:bottom-[-15rem] left-[calc(50%+6rem)] lg:left-[calc(50%+2rem)] h-[50rem] lg:h-[57.5rem] !scale-[1.06]"
                type="solana"
                style={{ y: ySolana }}
              />
              <CryptoPillar
                className="bottom-[11rem] lg:bottom-0 left-[calc(50%+16rem)] lg:left-[calc(50%+25rem)] h-[20rem] lg:h-[35rem] !scale-[1.04] lg:!scale-[1.08]"
                type="bitcoin"
                style={{ y: yBitcoin }}
              />
            </>
          )}

          <motion.div
            className={cn(
              "relative z-10",
              futures
                ? "w-[min(97.625rem,100%)] mt-[2rem] lg:mt-[5rem]"
                : "mt-[5rem] lg:mt-[10rem] lg:-mb-[10rem] w-[min(46.25rem,100%)] lg:w-[min(51.625rem,100%)] translate-x-[3rem] lg:translate-x-0",
            )}
            style={{ y: yArcher }}
          >
            <Image
              className="!w-full"
              src={futures ? futuresHero : knight}
              quality={100}
              alt="archer"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
